/**
 * Hook to get theme-specific assets with event overrides
 * Uses base theme assets and applies event-specific overrides when active
 */
import { useMemo } from 'react'
import { useTheme } from '../context/ThemeProvider'
import { getThemeName, getThemeMode } from '../utils/theme-utils'
import { useEventAssets } from './useEventAssets'

export interface SmallFlagImageUrls {
  [key: string]: string
}
export interface BannerFlagImageUrls {
  [key: string]: string
}

// Import base theme assets
// Gold theme assets
import {
  modelImageUrls as goldModelImageUrls,
  smallFlagImageUrls as goldSmallFlagImageUrls,
  backgroundFlagImageUrls as goldBackgroundFlagImageUrls,
  bannerFlagImageUrls as goldBannerFlagImageUrls,
  LOADING_ASSETS as goldLoadingAssets,
  SVG_ASSETS as goldSvgAssets,
} from '../constants/asset'

// Blue theme assets
import {
  modelImageUrlsBlue as blueModelImageUrls,
  smallFlagImageUrlsBlue as blueSmallFlagImageUrls,
  backgroundFlagImageUrlsBlue as blueBackgroundFlagImageUrls,
  bannerFlagImageUrlsBlue as blueBannerFlagImageUrls,
  bannerFlagImageUrlsBlueTh as blueBannerFlagImageUrlsTh,
  LOADING_ASSETS_BLUE as blueLoadingAssets,
  SVG_ASSETS_BLUE as blueSvgAssets,
} from '../constants/asset-blue'

import { ThemeName, ThemeMode } from '../constants/theme'
import { useTranslation } from 'react-i18next'

export const useThemeAssets = () => {
  const { theme } = useTheme()
  const themeName = getThemeName(theme)
  const themeMode = getThemeMode(theme)
  const { i18n } = useTranslation()
  const { event, eventAssets } = useEventAssets()

  // Get base theme assets
  const themeAssets = useMemo(() => {
    switch (themeName) {
      case ThemeName.BLUE:
        return {
          modelImageUrls: blueModelImageUrls,
          smallFlagImageUrls: blueSmallFlagImageUrls,
          backgroundFlagImageUrls: blueBackgroundFlagImageUrls,
          bannerFlagImageUrls:
            i18n.language === 'th'
              ? blueBannerFlagImageUrlsTh
              : blueBannerFlagImageUrls,
          loadingAssets: blueLoadingAssets,
          svgAssets: blueSvgAssets,
          themeName: ThemeName.BLUE,
        }
      case ThemeName.GOLD:
      default:
        return {
          modelImageUrls: goldModelImageUrls,
          smallFlagImageUrls: goldSmallFlagImageUrls,
          backgroundFlagImageUrls: goldBackgroundFlagImageUrls,
          bannerFlagImageUrls: goldBannerFlagImageUrls,
          loadingAssets: goldLoadingAssets,
          svgAssets: goldSvgAssets,
          themeName: ThemeName.GOLD,
        }
    }
  }, [themeName, i18n.language])

  // Apply event asset overrides for logo
  const svgAssetsWithEventOverride = useMemo(() => {
    const modeKey = themeMode === ThemeMode.LIGHT ? 'light' : 'dark'
    const defaultLogo = themeAssets.svgAssets.LOGO[modeKey]

    return {
      ...themeAssets.svgAssets,
      LOGO: {
        ...themeAssets.svgAssets.LOGO,
        [modeKey]: eventAssets.logo ?? defaultLogo,
      },
      // Add ribbon from event assets (Christmas)
      RIBBON: eventAssets.ribbon ?? null,
      // Add abstract border from event assets (CNY gold theme)
      ABSTRACT_BORDER: eventAssets.abstractBorder ?? null,
      // Add card lantern from event assets (CNY blue theme)
      CARD_LANTERN: eventAssets.cardLantern ?? null,
      // Add card flag banner from event assets (Songkran gold theme)
      CARD_FLAG_BANNER: eventAssets.cardFlagBanner ?? null,
      // Add chinese knot from event assets (CNY blue theme)
      CHINESE_KNOT: eventAssets.chineseKnot ?? null,
      // Add web decorations from event assets (Halloween)
      WEB_LEFT: eventAssets.webLeft ?? null,
      WEB_RIGHT: eventAssets.webRight ?? null,
    }
  }, [
    themeAssets.svgAssets,
    themeMode,
    eventAssets.logo,
    eventAssets.ribbon,
    eventAssets.abstractBorder,
    eventAssets.cardLantern,
    eventAssets.cardFlagBanner,
    eventAssets.chineseKnot,
    eventAssets.webLeft,
    eventAssets.webRight,
  ])

  // Apply event asset overrides for loading screens
  const loadingAssetsWithEventOverride = useMemo(() => {
    const defaultBackground = themeAssets.loadingAssets.background

    return {
      ...themeAssets.loadingAssets,
      background: eventAssets.loadingBackground ?? defaultBackground,
      backgrounds: {
        ...themeAssets.loadingAssets.backgrounds,
        ...(eventAssets.loadingBackgrounds ?? {}),
      },
    }
  }, [
    eventAssets.loadingBackground,
    eventAssets.loadingBackgrounds,
    themeAssets.loadingAssets,
  ])

  // Apply event asset overrides for model images
  const modelImageUrlsWithEventOverride = useMemo(() => {
    // If no event models, return base theme models
    if (!eventAssets.models) {
      return themeAssets.modelImageUrls
    }

    // Merge event models with base models (event models override)
    return {
      ...themeAssets.modelImageUrls,
      ...eventAssets.models,
    }
  }, [themeAssets.modelImageUrls, eventAssets.models])

  // Apply event asset overrides for banner flags (blue theme)
  const bannerFlagImageUrlsWithEventOverride = useMemo(() => {
    // Apply CNY banner flags for blue theme based on language
    if (i18n.language === 'th' && eventAssets.bannerFlagsTh) {
      return {
        ...themeAssets.bannerFlagImageUrls,
        ...eventAssets.bannerFlagsTh,
      }
    }
    if (i18n.language !== 'th' && eventAssets.bannerFlagsEn) {
      return {
        ...themeAssets.bannerFlagImageUrls,
        ...eventAssets.bannerFlagsEn,
      }
    }
    return themeAssets.bannerFlagImageUrls
  }, [
    themeAssets.bannerFlagImageUrls,
    eventAssets.bannerFlagsEn,
    eventAssets.bannerFlagsTh,
    i18n.language,
  ])

  // Apply banner flag fallback logic
  const bannerFlagImageUrlsWithFallback = createBannerFlagUrlsWithFallback(
    bannerFlagImageUrlsWithEventOverride,
    themeAssets.themeName,
  )

  // Apply small flag fallback logic
  const smallFlagImageUrlsWithFallback = createSmallFlagUrlsWithFallback(
    themeAssets.smallFlagImageUrls,
  )

  return {
    ...themeAssets,
    modelImageUrls: modelImageUrlsWithEventOverride,
    svgAssets: svgAssetsWithEventOverride,
    loadingAssets: loadingAssetsWithEventOverride,
    bannerFlagImageUrls: bannerFlagImageUrlsWithFallback,
    smallFlagImageUrls: smallFlagImageUrlsWithFallback,
    event,
  }
}

// Helper function to create banner flag URLs with fallback
const createBannerFlagUrlsWithFallback = (
  originalBannerFlagUrls: BannerFlagImageUrls,
  themeName: ThemeName,
): BannerFlagImageUrls => {
  // Create a proxy object that returns fallback for missing banners
  return new Proxy(originalBannerFlagUrls, {
    get(target, prop, receiver) {
      if (typeof prop !== 'string') {
        return Reflect.get(target, prop, receiver)
      }

      const value = Reflect.get(target, prop, receiver)

      // If the banner exists, return it
      if (value) {
        return value
      }

      // For blue theme, fallback to thai-government-bank-banner-flag
      if (themeName === ThemeName.BLUE) {
        const fallbackBanner = Reflect.get(
          target,
          'thai-government-bank-banner-flag',
          receiver,
        )
        if (fallbackBanner) {
          return fallbackBanner
        }
      }

      // For gold theme, fallback to stock banner
      if (themeName === ThemeName.GOLD) {
        const fallbackBanner = Reflect.get(target, 'stock', receiver)
        if (fallbackBanner) {
          return fallbackBanner
        }
      }

      // If no fallback available, return empty string to prevent undefined
      return ''
    },
  })
}

// Helper function to create small flag URLs with fallback
const createSmallFlagUrlsWithFallback = (
  originalSmallFlagUrls: SmallFlagImageUrls,
): SmallFlagImageUrls => {
  // Create a proxy object that returns fallback for missing small flags
  return new Proxy(originalSmallFlagUrls, {
    get(target, prop, receiver) {
      if (typeof prop !== 'string') {
        return Reflect.get(target, prop, receiver)
      }

      const value = Reflect.get(target, prop, receiver)

      // If the small flag exists, return it
      if (value) {
        return value
      }

      // For both themes, fallback to thailand small flag
      const fallbackFlag = Reflect.get(target, 'thailand', receiver)
      if (fallbackFlag) {
        return fallbackFlag
      }

      // If no fallback available, return empty string to prevent undefined
      return ''
    },
  })
}
